import i18n from '../i18n';
import { api } from '../services/api';
import { toast } from '../store/toastStore';
import { announce } from '../components/ui/LiveAnnouncer';
import { apiErrorMessage } from '../components/sales/shared';
import { UNDO_WINDOW_MS } from './undoableCommit';

/**
 * P-27 (D-166 s.11) - a credentials e-mail waits ~6 s on the server. Every
 * response that queued one carries `scheduled_emails`; this shows the toast
 * with "בטל" (stops the mail; the ACCESS and any temporary password stay) and,
 * after a cancel, "שליחה שוב" (a new mail with its own "בטל").
 */
export interface ScheduledMail {
  id: string;
  send_at?: string;
  delay_ms?: number;
  recipient_email?: string;
}

export function scheduledMailsOf(res: unknown): ScheduledMail[] {
  const list = (res as { scheduled_emails?: ScheduledMail[] } | null | undefined)?.scheduled_emails;
  return Array.isArray(list) ? list : [];
}

function showQueued(mail: ScheduledMail): void {
  toast('success', i18n.t('undoToast.mailQueued', { email: mail.recipient_email ?? '' }), {
    duration: mail.delay_ms ?? UNDO_WINDOW_MS,
    action: { label: i18n.t('common.undo'), onClick: () => { void cancel(mail); } },
  });
}

async function cancel(mail: ScheduledMail): Promise<void> {
  try {
    await api.cancelScheduledEmail(mail.id);
    announce(i18n.t('shell.undone'));
    toast('info', i18n.t('undoToast.mailCancelled'), {
      duration: 10_000,
      action: { label: i18n.t('undoToast.mailResend'), onClick: () => { void resend(mail); } },
    });
  } catch (err) {
    const status = (err as { response?: { status?: number }; error?: { code?: number } })?.response?.status;
    if (status === 409) toast('warning', i18n.t('undoToast.mailAlreadySent'));
    else toast('error', apiErrorMessage(err, i18n.t('undoToast.failed')));
  }
}

// DQ-240: one resend per mail at a time (a double click on "שליחה שוב" sends one mail).
const resending = new Set<string>();

async function resend(mail: ScheduledMail): Promise<void> {
  if (resending.has(mail.id)) return;
  resending.add(mail.id);
  try {
    const res = await api.resendScheduledEmail(mail.id);
    showQueued({ ...mail, ...(res as { data?: ScheduledMail }).data });
  } catch (err) {
    toast('error', apiErrorMessage(err, i18n.t('undoToast.failed')));
  } finally {
    resending.delete(mail.id);
  }
}

/**
 * Show the "בטל" toast for every credentials mail the response queued.
 * Returns true when it handled the response (the caller then skips its old
 * "המייל נשלח" toast), false when nothing was queued (old behaviour).
 */
export function reportScheduledMail(res: unknown): boolean {
  const mails = scheduledMailsOf(res);
  mails.forEach(showQueued);
  return mails.length > 0;
}
